/**
 * EnergyPerformanceUniversalTable.tsx
 *
 * Replaces energy-performance-table.tsx (now in deprecated/).
 * Built on UniversalTable with CellConfig[] cell-based rendering.
 *
 * NOTE: Pagination intentionally deferred — charts in energieprestaties.tsx
 * require the full dataset. Client-side filtering via UniversalTable internals
 * is acceptable at current volume. Revisit only if profiling shows > 16ms render.
 *
 * New columns (2026-05-12):
 *   - GEÏNVESTEERD: shows planned or actueel € with energetisch sub-line
 *   - LABELS: project part label chips by category color
 */
import { useState, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Download } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { FileLabel } from "shared/types/file";
import { Button } from "@/components/ui/button";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import EPCMetadataModal from "shared/components/epcMetadataModal/epcMetadataModal";
import DeleteConfirmationModal from "shared/components/modals/deleteConfirmationModal";
import { UniversalTable } from "shared/components/UniversalTable";
import { useExcelExport } from "shared/components/UniversalTable/hooks/useExcelExport";
import type { CellConfig } from "shared/components/UniversalTable/components/UniversalTableRow";
import type { ColumnDefinition } from "shared/components/UniversalTable/types/ColumnConfig";
import type { EPCData } from "shared/components/maintenance/hooks/useEnergyPerformanceData";
import type { InvestmentMode } from "shared/components/maintenance/components/EnergyHeroKpiCards";
import EpcRefreshSuggestionModal from "../modals/EpcRefreshSuggestionModal";
import { BuildingGroupHeader } from "./BuildingGroupHeader";
import {
  LocationCell,
  CurrentEpcCell,
  EvolutionCell,
  ProjectPartsCell,
  RefreshSuggestedCell,
  InvestedCell,
} from "./cells";
import { formatDateForExport, getPartEffectiveDate } from "./cells/utils";

// ─── Column definitions ───────────────────────────────────────────────────────

const COLUMNS: ColumnDefinition[] = [
  {
    id: "location",
    label: "LOCATIE",
    minWidth: 180,
    defaultWeight: 4,
    sortKey: "location",
  },
  {
    id: "type",
    label: "TYPE",
    minWidth: 110,
    defaultWeight: 2,
    sortKey: "unitType",
  },
  {
    id: "currentEpc",
    label: "HUIDIGE EPC",
    minWidth: 150,
    defaultWeight: 3,
    sortKey: "currentEPC.label",
  },
  {
    id: "evolution",
    label: "EPC EVOLUTIE",
    minWidth: 180,
    defaultWeight: 4,
  },
  {
    id: "projectParts",
    label: "LABELS",
    minWidth: 200,
    defaultWeight: 5,
  },
  {
    id: "invested",
    label: "GEÏNVESTEERD",
    minWidth: 150,
    defaultWeight: 3,
    sortKey: "investment.plannedTotal",
  },
  {
    id: "refreshSuggested",
    label: "NIEUWE EPC NODIG",
    minWidth: 160,
    defaultWeight: 2,
    sortKey: "epcRefreshSuggested",
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  data: EPCData[];
  investmentMode: InvestmentMode;
  onRefreshSingleEPC: (fileLogId: number) => Promise<void>;
  onDeleteEPC: (fileLogId: number) => Promise<boolean>;
  onRefresh: () => void;
}

// ─── Excel helpers ────────────────────────────────────────────────────────────

function buildProjectPartsFlat(row: EPCData): string {
  return row.projectPartGroups
    .map((g) => {
      const partsStr = g.parts
        .map((p) => {
          const dateStr = formatDateForExport(getPartEffectiveDate(p));
          return dateStr ? `${p.title} (${dateStr})` : p.title;
        })
        .join(", ");
      return `${g.projectTitle}: ${partsStr}`;
    })
    .join(" | ");
}

function buildEpcHistoryFlat(row: EPCData): string {
  if (!row.epcHistory || row.epcHistory.length === 0) return "";
  const sorted = [...row.epcHistory].sort((a, b) => {
    if (!a.documentDate && !b.documentDate) return 0;
    if (!a.documentDate) return -1;
    if (!b.documentDate) return 1;
    return (
      new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime()
    );
  });
  return sorted
    .map((epc) => {
      const dateStr = formatDateForExport(epc.documentDate);
      const kwhStr = epc.kWh ? `, ${epc.kWh} kWh` : "";
      return `${epc.label} (${dateStr}${kwhStr})`;
    })
    .join(" → ");
}

// ─── Component ────────────────────────────────────────────────────────────────

// ─── ViewMode persistence helpers ────────────────────────────────────────────

const VIEW_MODE_KEY = "energyperformance-viewmode";

function getStoredViewMode(): "byBuilding" | "flat" {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "byBuilding" || v === "flat") return v;
  } catch {
    // localStorage unavailable
  }
  return "byBuilding";
}

function storeViewMode(mode: "byBuilding" | "flat"): void {
  try {
    localStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // localStorage unavailable
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EnergyPerformanceUniversalTable = ({
  data,
  investmentMode,
  onRefreshSingleEPC,
  onDeleteEPC,
  onRefresh,
}: Props) => {
  const { session } = useSession();
  const { exportToExcel, isExporting } = useExcelExport();

  // View mode state — persisted to localStorage
  const [viewMode, setViewMode] = useState<"byBuilding" | "flat">(getStoredViewMode);

  const handleViewModeChange = useCallback((mode: "byBuilding" | "flat") => {
    setViewMode(mode);
    storeViewMode(mode);
  }, []);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadContext, setUploadContext] = useState<{
    building?: any;
    unit?: any;
    label: string;
  } | null>(null);

  // EPC metadata modal state
  const [epcMetadataModalOpen, setEpcMetadataModalOpen] = useState(false);
  const [epcMetadataContext, setEpcMetadataContext] = useState<{
    fileLogId: number;
    currentEPC?: any;
    unitType: "unit" | "building";
  } | null>(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [epcToDelete, setEpcToDelete] = useState<{
    fileLogId: number;
    unitLocation: string;
    epcLabel: string;
    documentDate: string;
    kWh?: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refresh suggestion modal state
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshModalUnit, setRefreshModalUnit] = useState<EPCData | null>(null);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleUploadEPC = useCallback(
    (buildingId?: string, unitId?: string) => {
      const label = unitId ? FileLabel.EPCUnit : FileLabel.EPCCommon;
      let building = undefined;
      let unit = undefined;
      if (buildingId) {
        const found = data.find((item) => item.buildingId === buildingId);
        if (found) building = { id: buildingId };
      }
      if (unitId) {
        const unitData = data.find((item) => item.unitId === unitId);
        if (unitData) {
          unit = { id: unitId };
          if (!building && unitData.buildingId) {
            building = { id: unitData.buildingId };
          }
        }
      }
      setUploadContext({ building, unit, label: label as FileLabel });
      setUploadModalOpen(true);
    },
    [data],
  );

  const handleUploadComplete = useCallback(() => {
    setUploadModalOpen(false);
    setUploadContext(null);
    toast.success("EPC succesvol opgeladen!");
    onRefresh();
  }, [onRefresh]);

  const handleEditSpecificEPC = useCallback(
    async (fileLogId: number, unit: EPCData) => {
      try {
        let currentEPC = null;
        try {
          const response = await fetchWithAuth(
            `${API_URL}/file/file-log-with-epc/${fileLogId}`,
            { method: "GET" },
            session,
          );
          if (response.ok) {
            const data = await response.json();
            currentEPC = data.epc;
          }
        } catch {
          // No existing EPC data — will create new
        }
        const unitType =
          unit.unitType === "Gemeenschappelijke delen" ? "building" : "unit";
        setEpcMetadataContext({ fileLogId, currentEPC, unitType });
        setEpcMetadataModalOpen(true);
      } catch (error) {
        console.error(
          "[EnergyPerformanceUniversalTable] Error preparing EPC metadata edit:",
          error,
        );
        toast.error("Fout bij het voorbereiden van EPC gegevens bewerking");
      }
    },
    [session],
  );

  const handleEPCMetadataComplete = useCallback(() => {
    setEpcMetadataModalOpen(false);
    toast.success("EPC gegevens succesvol bijgewerkt!");
    if (epcMetadataContext?.fileLogId) {
      onRefreshSingleEPC(epcMetadataContext.fileLogId);
    }
    setEpcMetadataContext(null);
  }, [epcMetadataContext, onRefreshSingleEPC]);

  const handleDeleteEPC = useCallback(
    (
      fileLogId: number,
      unitLocation: string,
      epcLabel?: string,
      documentDate?: string,
      kWh?: string,
    ) => {
      setEpcToDelete({
        fileLogId,
        unitLocation,
        epcLabel: epcLabel || "Onbekend",
        documentDate: documentDate || "",
        kWh,
      });
      setDeleteModalOpen(true);
    },
    [],
  );

  const confirmDeleteEPC = useCallback(async () => {
    if (!epcToDelete) return;
    setIsDeleting(true);
    try {
      const success = await onDeleteEPC(epcToDelete.fileLogId);
      if (success) {
        setDeleteModalOpen(false);
        setEpcToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [epcToDelete, onDeleteEPC]);

  const handleOpenRefreshModal = useCallback((unit: EPCData) => {
    setRefreshModalUnit(unit);
    setRefreshModalOpen(true);
  }, []);

  // ─── Excel export ───────────────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    try {
      const epcEvolutionDisplay = (
        v: EPCData["epcEvolution"],
      ): string =>
        v === "improved"
          ? "Verbeterd"
          : v === "declined"
            ? "Verslechterd"
            : v === "unchanged"
              ? "Onveranderd"
              : "";
      const epcReasonDisplay = (r: EPCData["epcRefreshReason"]): string =>
        r === "ENERGETIC"
          ? "Energetisch"
          : r === "GENERIC"
            ? "Algemeen"
            : "";

      const flattenedRows = data.map((row) => ({
        unitLocation: row.unitLocation || row.location || "",
        buildingLocation: row.buildingLocation || "",
        unitType: row.unitType || row.type || "",
        currentEpcLabel: row.currentEPC?.label ?? "",
        currentEpcKwh: row.currentEPC?.kWh ?? "",
        currentEpcDate: formatDateForExport(row.currentEPC?.documentDate),
        epcEvolutionDisplay: epcEvolutionDisplay(row.epcEvolution),
        epcHistoryFlat: buildEpcHistoryFlat(row),
        latestEpcDocumentDate: formatDateForExport(row.latestEpcDocumentDate),
        projectPartsFlat: buildProjectPartsFlat(row),
        latestProjectPartDate: formatDateForExport(row.latestProjectPartDate),
        investedPlanned: row.investment.plannedTotal,
        investedActual: row.investment.actualTotal,
        investedEnergetisch: row.investment.byCategory.energetisch.planned,
        investedEsthetisch: row.investment.byCategory.esthetisch.planned,
        investedVeiligheid: row.investment.byCategory.veiligheid.planned,
        investedAlgemeen: row.investment.byCategory.algemeen.planned,
        epcRefreshNeeded: row.epcRefreshSuggested ? "Ja" : "Nee",
        epcRefreshReason: epcReasonDisplay(row.epcRefreshReason),
      }));

      // Dedicated export columns — every entry must have a sortKey so the BE
      // exporter (excel.service.ts) doesn't filter it out.
      const exportColumns: ColumnDefinition[] = [
        { id: "unitLocation", label: "LOCATIE", minWidth: 0, defaultWeight: 1, sortKey: "unitLocation" },
        { id: "buildingLocation", label: "GEBOUW", minWidth: 0, defaultWeight: 1, sortKey: "buildingLocation" },
        { id: "unitType", label: "TYPE", minWidth: 0, defaultWeight: 1, sortKey: "unitType" },
        { id: "currentEpcLabel", label: "HUIDIGE EPC", minWidth: 0, defaultWeight: 1, sortKey: "currentEpcLabel" },
        { id: "currentEpcKwh", label: "kWh/m²/jaar", minWidth: 0, defaultWeight: 1, sortKey: "currentEpcKwh" },
        { id: "currentEpcDate", label: "EPC DATUM", minWidth: 0, defaultWeight: 1, sortKey: "currentEpcDate" },
        { id: "epcEvolutionDisplay", label: "EPC EVOLUTIE", minWidth: 0, defaultWeight: 1, sortKey: "epcEvolutionDisplay" },
        { id: "epcHistoryFlat", label: "EPC HISTORIEK", minWidth: 0, defaultWeight: 1, sortKey: "epcHistoryFlat" },
        { id: "latestEpcDocumentDate", label: "LAATSTE EPC", minWidth: 0, defaultWeight: 1, sortKey: "latestEpcDocumentDate" },
        { id: "projectPartsFlat", label: "RENOVATIEWERKEN", minWidth: 0, defaultWeight: 1, sortKey: "projectPartsFlat" },
        { id: "latestProjectPartDate", label: "LAATSTE WERKEN", minWidth: 0, defaultWeight: 1, sortKey: "latestProjectPartDate" },
        { id: "investedPlanned", label: "GEÏNVESTEERD (PLANNED)", minWidth: 0, defaultWeight: 1, sortKey: "investedPlanned" },
        { id: "investedActual", label: "GEÏNVESTEERD (ACTUEEL)", minWidth: 0, defaultWeight: 1, sortKey: "investedActual" },
        { id: "investedEnergetisch", label: "INV. ENERGETISCH", minWidth: 0, defaultWeight: 1, sortKey: "investedEnergetisch" },
        { id: "investedEsthetisch", label: "INV. ESTHETISCH", minWidth: 0, defaultWeight: 1, sortKey: "investedEsthetisch" },
        { id: "investedVeiligheid", label: "INV. VEILIGHEID", minWidth: 0, defaultWeight: 1, sortKey: "investedVeiligheid" },
        { id: "investedAlgemeen", label: "INV. ALGEMEEN", minWidth: 0, defaultWeight: 1, sortKey: "investedAlgemeen" },
        { id: "epcRefreshNeeded", label: "NIEUWE EPC NODIG", minWidth: 0, defaultWeight: 1, sortKey: "epcRefreshNeeded" },
        { id: "epcRefreshReason", label: "REDEN", minWidth: 0, defaultWeight: 1, sortKey: "epcRefreshReason" },
      ];

      await exportToExcel(flattenedRows, exportColumns, [], {
        filename: "energieprestaties",
        sheetName: "Energieprestaties",
        currencyColumns: [
          "investedPlanned",
          "investedActual",
          "investedEnergetisch",
          "investedEsthetisch",
          "investedVeiligheid",
          "investedAlgemeen",
        ],
        freezeColumns: 1,
      });
    } catch (error) {
      console.error(
        "[EnergyPerformanceUniversalTable] Excel export error:",
        error,
      );
      toast.error("Fout bij het exporteren naar Excel");
    }
  }, [data, exportToExcel]);

  // ─── Row renderer ───────────────────────────────────────────────────────────

  const renderRow = useCallback(
    (unit: EPCData): CellConfig[] => {
      return [
        {
          id: "location",
          content: (
            <LocationCell
              unit={unit}
              onOpenRefreshModal={() => handleOpenRefreshModal(unit)}
            />
          ),
        },
        {
          id: "type",
          content: (
            <span
              className={`rounded px-2 py-1 text-xs ${
                unit.unitType === "Residentieel"
                  ? "bg-moss-primary text-white"
                  : unit.unitType === "Commercieel"
                    ? "bg-brick-primary text-white"
                    : "bg-neutral-secondary-mid text-white"
              }`}
            >
              {unit.unitType}
            </span>
          ),
        },
        {
          id: "currentEpc",
          content: (
            <CurrentEpcCell
              unit={unit}
              onEdit={handleEditSpecificEPC}
              onDelete={handleDeleteEPC}
              onUpload={handleUploadEPC}
            />
          ),
        },
        {
          id: "evolution",
          content: (
            <EvolutionCell
              unit={unit}
              onEdit={handleEditSpecificEPC}
              onDelete={handleDeleteEPC}
            />
          ),
        },
        {
          id: "projectParts",
          content: (
            <ProjectPartsCell projectPartGroups={unit.projectPartGroups ?? []} />
          ),
        },
        {
          id: "invested",
          content: <InvestedCell unit={unit} mode={investmentMode} />,
        },
        {
          id: "refreshSuggested",
          content: (
            <RefreshSuggestedCell
              unit={unit}
              onOpenRefreshModal={() => handleOpenRefreshModal(unit)}
            />
          ),
        },
      ];
    },
    [
      handleEditSpecificEPC,
      handleDeleteEPC,
      handleUploadEPC,
      handleOpenRefreshModal,
      investmentMode,
    ],
  );

  // ─── Header extra (view toggle + Excel export button) ─────────────────────

  const headerExtra = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {/* Per gebouw / Plat toggle */}
        <div className="flex items-center rounded-md border border-neutral-primary/15 text-xs">
          <button
            type="button"
            className={`rounded-l-md px-2.5 py-1 transition-colors ${
              viewMode === "byBuilding"
                ? "bg-neutral-primary text-white"
                : "text-neutral-secondary-mid hover:bg-neutral-primary/5"
            }`}
            onClick={() => handleViewModeChange("byBuilding")}
          >
            Per gebouw
          </button>
          <button
            type="button"
            className={`rounded-r-md px-2.5 py-1 transition-colors ${
              viewMode === "flat"
                ? "bg-neutral-primary text-white"
                : "text-neutral-secondary-mid hover:bg-neutral-primary/5"
            }`}
            onClick={() => handleViewModeChange("flat")}
          >
            Per unit
          </button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting}
          className="gap-2"
        >
          <Download className="h-4 w-4" weight="bold" />
          {isExporting ? "Exporteren..." : "Exporteer naar Excel"}
        </Button>
      </div>
    ),
    [handleExport, isExporting, viewMode, handleViewModeChange],
  );

  // ─── Building group header renderer ────────────────────────────────────────

  const renderGroupHeader = useCallback(
    (
      buildingId: string,
      groupRows: EPCData[],
      context?: { templateColumns: string },
    ) => (
      <BuildingGroupHeader
        buildingId={buildingId}
        rows={groupRows}
        investmentMode={investmentMode}
        templateColumns={context?.templateColumns}
      />
    ),
    [investmentMode],
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  const columnsConfig = useMemo(
    () => ({
      columns: COLUMNS,
      localStorageKey: "energy-performance-table-widths",
    }),
    [],
  );

  return (
    <>
      <UniversalTable
        rows={data}
        title="Energieprestaties"
        renderRow={renderRow}
        columnsConfig={columnsConfig}
        tableId="energy-performance"
        headerExtra={headerExtra}
        getRowKey={(row) => row.unitId}
        groupBy={viewMode === "byBuilding" ? "buildingId" : "none"}
        renderGroupHeader={renderGroupHeader}
      />

      {/* Upload Modal */}
      {uploadModalOpen && uploadContext && (
        <ModalUploadFileLog
          isOpen={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          building={uploadContext.building}
          unit={uploadContext.unit}
          defaultFileLabel={uploadContext.label as FileLabel}
          onFileUploadSuccess={handleUploadComplete}
        />
      )}

      {/* EPC Metadata Modal */}
      {epcMetadataModalOpen && epcMetadataContext && (
        <EPCMetadataModal
          isOpen={epcMetadataModalOpen}
          onClose={() => setEpcMetadataModalOpen(false)}
          fileLogId={epcMetadataContext.fileLogId}
          currentEPC={epcMetadataContext.currentEPC}
          unitType={epcMetadataContext.unitType}
          onSuccess={handleEPCMetadataComplete}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && epcToDelete && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => {
            if (!isDeleting) {
              setDeleteModalOpen(false);
              setEpcToDelete(null);
            }
          }}
          onConfirm={confirmDeleteEPC}
          title="EPC verwijderen"
          description={`Weet je zeker dat je de EPC voor ${epcToDelete.unitLocation} wilt verwijderen?`}
          itemName={`EPC ${epcToDelete.epcLabel}`}
          itemDetails={[
            `Locatie: ${epcToDelete.unitLocation}`,
            `EPC Label: ${epcToDelete.epcLabel}`,
            ...(epcToDelete.documentDate
              ? [
                  `Documentdatum: ${new Date(epcToDelete.documentDate).toLocaleDateString("nl-NL")}`,
                ]
              : []),
            ...(epcToDelete.kWh
              ? [`Energieverbruik: ${epcToDelete.kWh} kWh/m²/jaar`]
              : []),
          ]}
          isLoading={isDeleting}
          confirmText="EPC verwijderen"
        />
      )}

      {/* EPC Refresh Suggestion Modal */}
      {refreshModalOpen && refreshModalUnit && (
        <EpcRefreshSuggestionModal
          isOpen={refreshModalOpen}
          onClose={() => {
            setRefreshModalOpen(false);
            setRefreshModalUnit(null);
          }}
          currentEpcLabel={refreshModalUnit.currentEPC?.label ?? null}
          latestEpcDocumentDate={refreshModalUnit.latestEpcDocumentDate}
          latestProjectPartDate={refreshModalUnit.latestProjectPartDate}
          epcRefreshReason={refreshModalUnit.epcRefreshReason}
          projectPartGroups={refreshModalUnit.projectPartGroups ?? []}
          onUploadEpc={() =>
            handleUploadEPC(
              refreshModalUnit.buildingId,
              refreshModalUnit.unitId !==
                `${refreshModalUnit.buildingId}-gemeenschap`
                ? refreshModalUnit.unitId
                : undefined,
            )
          }
        />
      )}
    </>
  );
};

export default EnergyPerformanceUniversalTable;
